import type { PartnerLegalAgreementStateChange } from '@shipium-corp/shipium-ts-types';

/**
 * The set of legal-agreement types the Console can present. Sourced from the shared
 * shipium-ts-types contract so it stays in sync with Partner Management.
 */
export type LegalAgreementType = PartnerLegalAgreementStateChange['legalAgreementType'];

export interface CarrierEulaConfig {
  /** The legal-agreement type persisted in Partner Management. */
  legalAgreementType: LegalAgreementType;
  /** Page/heading title. */
  title: string;
  /** Path to the carrier brand logo (served from /public). */
  logoSrc: string;
  /** Accessible alt text for the logo. */
  logoAlt: string;
  /**
   * Verbatim service-mark / legal notice rendered on every screen showing the carrier's marks.
   * For FedEx this text is mandated by the FedEx brand guide and must not be altered.
   */
  disclaimerText: string;
  /**
   * Clickwrap consent label shown next to the acceptance checkbox. Carrier-specific: each
   * agreement must declare its own approved wording rather than inherit another carrier's.
   */
  acceptanceLabel: string;
  /** Path to the full EULA PDF (served from /public), shown inline with scroll-to-accept. */
  eulaPdfPath: string;
  /**
   * Current required EULA version. When set, acceptance is only honored if the stored accepted
   * version matches this value — a bump here re-prompts the partner. Leave undefined for
   * version-agnostic agreements (e.g. legacy offline labels).
   */
  eulaVersion?: string;
  /** Optional docs link shown in the page header. */
  docsUrl?: string;
  /** Short informational message shown above the agreement. */
  description: string;
  /**
   * When true, the agreement is registered (for compile-time exhaustiveness) but not yet usable —
   * e.g. its EULA artifact is not finalized. The page renders an "unavailable" notice instead of
   * the clickwrap so no one can accept against a missing/placeholder document.
   */
  disabled?: boolean;
}

/**
 * Verbatim FedEx service-mark notice. Required at 8pt+ on every screen displaying FedEx marks
 * (FedEx UK-EUROPE Verification Instruction Guide). Do not alter this string.
 */
export const FEDEX_SERVICE_MARK_DISCLAIMER =
  'FedEx service marks are owned by Federal Express Corporation and are used by permission.';

/**
 * Verbatim FedEx-legal-approved clickwrap consent wording (CR-686). It affirms the partner has
 * read the agreement, not merely that they accept it. Do not alter without FedEx legal sign-off.
 */
export const FEDEX_EULA_ACCEPT_LABEL = 'I have read and accept the FedEx EULA terms';

/**
 * Per-carrier/region brand + EULA configuration. Keyed by legal-agreement type so the union is
 * exhaustively configured at compile time.
 *
 * NOTE on artifacts: LATAM uses the FedEx Hosted/ASP EULA (#2002382 v4A). The EU EULA document and
 * version ship with the EU mini-cert package and are pending confirmation from the FedEx
 * Verification Lead — the placeholder below must be replaced (and `eulaVersion` set) before the EU
 * page is used for Brand-Legal sign-off.
 */
export const CARRIER_EULA_CONFIGS: Record<LegalAgreementType, CarrierEulaConfig> = {
  FEDEX_OFFLINE_LABELS_EULA: {
    legalAgreementType: 'FEDEX_OFFLINE_LABELS_EULA',
    title: 'FedEx Offline Labels End-User License Agreement',
    logoSrc: '/fedex_logo.png',
    logoAlt: 'FedEx Logo',
    disclaimerText: FEDEX_SERVICE_MARK_DISCLAIMER,
    acceptanceLabel: FEDEX_EULA_ACCEPT_LABEL,
    eulaPdfPath: '/fedex_standard_end_user_license_agreement_eula_for_distributed_automation_products.pdf',
    docsUrl: 'https://docs.shipium.com/docs/offline-labels',
    description: 'This agreement is required to use FedEx Ship Manager® Server for offline labels.',
  },
  FEDEX_INTL_LATAM_EULA: {
    legalAgreementType: 'FEDEX_INTL_LATAM_EULA',
    title: 'FedEx End-User License Agreement',
    logoSrc: '/fedex_logo.png',
    logoAlt: 'FedEx Logo',
    disclaimerText: FEDEX_SERVICE_MARK_DISCLAIMER,
    acceptanceLabel: FEDEX_EULA_ACCEPT_LABEL,
    // FedEx Hosted/ASP EULA (#2002382 v4A) — the version that applies to Shipium (Hosted/ASP).
    eulaPdfPath: '/fedex_eula_asp.pdf',
    eulaVersion: '4A',
    description: 'FedEx requires acceptance of this agreement to enable FedEx International (LATAM) shipping.',
  },
  FEDEX_INTL_EU_EULA: {
    legalAgreementType: 'FEDEX_INTL_EU_EULA',
    title: 'FedEx End-User License Agreement',
    logoSrc: '/fedex_logo.png',
    logoAlt: 'FedEx Logo',
    disclaimerText: FEDEX_SERVICE_MARK_DISCLAIMER,
    acceptanceLabel: FEDEX_EULA_ACCEPT_LABEL,
    // TODO(CR-374): replace with the EU mini-cert EULA document and set eulaVersion once the
    // FedEx Verification Lead confirms the artifact/version, then remove `disabled`.
    eulaPdfPath: '/fedex_eula_eu.pdf',
    eulaVersion: undefined,
    description: 'FedEx requires acceptance of this agreement to enable FedEx International (UK/Europe) shipping.',
    // Not yet usable: the EU EULA PDF/version is pending the FedEx Verification Lead.
    disabled: true,
  },
};

/** Returns the brand + EULA configuration for a legal-agreement type. */
export const getCarrierEulaConfig = (type: LegalAgreementType): CarrierEulaConfig => CARRIER_EULA_CONFIGS[type];

/** Type guard: whether an arbitrary string is a configured legal-agreement type. */
export const isKnownAgreementType = (value: string | undefined): value is LegalAgreementType =>
  value != null && Object.prototype.hasOwnProperty.call(CARRIER_EULA_CONFIGS, value);
